import React, { useEffect, useState } from 'react';
import { fetchPublicCmsConfig, invalidateCmsConfigCache } from '../utils/cmsConfigCache';

// Google fonts that we consider "web safe enough" to load automatically.
// Anything outside this list is treated as a system-installed font.
const GOOGLE_FONT_WHITELIST = new Set([
  // Sans-serif
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Raleway',
  'Ubuntu', 'Nunito', 'Nunito Sans', 'Source Sans 3', 'Source Sans Pro', 'Oswald',
  'Work Sans', 'Rubik', 'Fira Sans', 'Quicksand', 'Josefin Sans', 'Archivo',
  'Archivo Black', 'PT Sans', 'Karla', 'Manrope', 'DM Sans',
  'Space Grotesk', 'Plus Jakarta Sans', 'Barlow', 'Barlow Condensed',
  'IBM Plex Sans', 'Outfit', 'Comfortaa', 'Anton', 'Bebas Neue',
  // Serif / display
  'Playfair Display', 'Merriweather', 'Lora', 'Cormorant Garamond',
  'Crimson Pro', 'EB Garamond', 'Libre Baskerville', 'PT Serif',
  'Source Serif 4', 'Roboto Slab', 'DM Serif Display', 'IBM Plex Serif',
  // Monospace
  'Fira Code', 'JetBrains Mono', 'Space Mono', 'Roboto Mono',
  // Handwriting / fun
  'Dancing Script', 'Caveat', 'Pacifico', 'Permanent Marker',
  'Press Start 2P', 'Indie Flower', 'Shadows Into Light',
]);

const ensureGoogleFont = (family) => {
  if (!family || family === 'inherit' || family === 'system-ui') return;
  const base = family.split(',')[0].replace(/['"]/g, '').trim();
  if (base === 'Plus Jakarta Sans') return;
  if (!GOOGLE_FONT_WHITELIST.has(base)) return;
  const id = `gf-${base.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(base).replace(/%20/g, '+')}:wght@300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
};

const applyTheme = (theme) => {
  if (!theme) return;
  const root = document.documentElement;
  const colors = theme.colors || {};
  const fonts = theme.fonts || {};
  const radius = theme.borderRadius || {};
  const shadows = theme.shadows || {};

  // Colors
  Object.entries(colors).forEach(([key, value]) => {
    if (value) root.style.setProperty(`--theme-${key}`, value);
  });

  // Fonts
  if (fonts.heading) {
    root.style.setProperty('--theme-font-heading', `"${fonts.heading}", sans-serif`);
    ensureGoogleFont(fonts.heading);
  }
  if (fonts.body) {
    root.style.setProperty('--theme-font-body', `"${fonts.body}", sans-serif`);
    ensureGoogleFont(fonts.body);
  }
  if (fonts.headingWeight) root.style.setProperty('--theme-font-heading-weight', fonts.headingWeight);
  if (fonts.bodyWeight) root.style.setProperty('--theme-font-body-weight', fonts.bodyWeight);

  // Radius
  if (radius.small) root.style.setProperty('--theme-radius-sm', radius.small);
  if (radius.medium) root.style.setProperty('--theme-radius-md', radius.medium);
  if (radius.large) root.style.setProperty('--theme-radius-lg', radius.large);
  if (radius.xl) root.style.setProperty('--theme-radius-xl', radius.xl);

  // Shadows
  if (shadows.cardShadow) root.style.setProperty('--theme-shadow-card', shadows.cardShadow);
  if (shadows.buttonShadow) root.style.setProperty('--theme-shadow-button', shadows.buttonShadow);
  if (shadows.hoverShadow) root.style.setProperty('--theme-shadow-hover', shadows.hoverShadow);
};

// Inject/update a <link> tag in <head> by rel attribute
const setFavicon = (url) => {
  if (!url) return;
  // Remove all existing favicons to avoid conflicts (public/favicon.ico, old React defaults, etc.)
  document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach(l => l.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  // Detect ICO vs PNG/SVG to set correct mime
  const lower = url.toLowerCase();
  if (lower.includes('.ico') || lower.includes('image/x-icon') || lower.includes('image/vnd.microsoft.icon')) {
    link.type = 'image/x-icon';
  } else if (lower.endsWith('.svg') || lower.includes('image/svg')) {
    link.type = 'image/svg+xml';
  } else {
    link.type = 'image/png';
  }
  link.href = url;
  document.head.appendChild(link);

  // Apple-touch-icon (for iOS home screen)
  const apple = document.createElement('link');
  apple.rel = 'apple-touch-icon';
  apple.href = url;
  document.head.appendChild(apple);
};

// Inject/update a <meta> tag by property or name attribute
const setMetaTag = (attr, key, content) => {
  if (!content) {
    document.querySelectorAll(`meta[${attr}="${key}"]`).forEach(m => m.remove());
    return;
  }
  let meta = document.querySelector(`meta[${attr}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Absolute URL helper (OG images must be absolute for some platforms)
const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const applyMetadata = (metadata) => {
  if (!metadata) return;
  // Title
  if (metadata.title) {
    document.title = metadata.title;
    setMetaTag('property', 'og:title', metadata.title);
    setMetaTag('name', 'twitter:title', metadata.title);
  }
  // Description
  if (metadata.description) {
    setMetaTag('name', 'description', metadata.description);
    setMetaTag('property', 'og:description', metadata.description);
    setMetaTag('name', 'twitter:description', metadata.description);
  }
  // Favicon
  if (metadata.favicon) setFavicon(metadata.favicon);
  // Open Graph image (social media preview)
  if (metadata.socialImage) {
    const abs = toAbsoluteUrl(metadata.socialImage);
    setMetaTag('property', 'og:image', abs);
    setMetaTag('property', 'og:image:width', '1200');
    setMetaTag('property', 'og:image:height', '630');
    setMetaTag('name', 'twitter:image', abs);
    setMetaTag('name', 'twitter:card', 'summary_large_image');
  }
  // og:url + og:type
  setMetaTag('property', 'og:url', window.location.href);
  setMetaTag('property', 'og:type', 'website');
  if (metadata.title) setMetaTag('property', 'og:site_name', metadata.title);
};

// Google Analytics is now injected SERVER-SIDE into index.html (see seo_utils.py).
// This means Google's verification crawler (Tag Assistant) and any non-JS bot
// can detect the gtag.js script in the initial HTML response.
//
// All this function does on the client is:
//  1. If the user has previously accepted cookies, immediately call
//     gtag('consent','update',...) so analytics_storage flips to 'granted'.
//  2. (Re-)called whenever the consent state changes via 'cookie-consent-updated'.
const syncGoogleAnalyticsConsent = (gaId) => {
  if (!gaId || !gaId.startsWith('G-')) return;
  let consentGranted = false;
  try {
    const consent = JSON.parse(localStorage.getItem('cookie_consent') || '{}');
    consentGranted = consent?.type === 'all';
  } catch {}

  // gtag may not exist yet if the static <script> tag hasn't executed.
  // Retry a couple of times to catch the race.
  let tries = 0;
  const apply = () => {
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('consent', 'update', {
          ad_storage: consentGranted ? 'granted' : 'denied',
          ad_user_data: consentGranted ? 'granted' : 'denied',
          ad_personalization: consentGranted ? 'granted' : 'denied',
          analytics_storage: consentGranted ? 'granted' : 'denied',
        });
      } catch {}
      return;
    }
    if (tries++ < 10) setTimeout(apply, 300);
  };
  apply();
};

// Listens to cms config and applies theme globally (CSS variables + Google Fonts)
const ThemeApplier = () => {
  const [theme, setTheme] = useState(null);
  const [navFont, setNavFont] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [gaId, setGaId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (forceFresh = false) => {
      try {
        if (forceFresh) invalidateCmsConfigCache();
        const data = await fetchPublicCmsConfig();
        if (cancelled) return;
        setTheme(data?.theme || null);
        setNavFont(data?.navigation?.style?.fontFamily || null);
        setMetadata(data?.metadata || null);
        setGaId(data?.analytics?.gaId || null);
      } catch (e) {
        // silent
      }
    };
    load();
    // Re-check every 30s so admin changes propagate without reload
    const iv = setInterval(() => load(true), 30000);
    // Also react to admin save events (same tab)
    const onSave = () => load(true);
    window.addEventListener('cms-config-updated', onSave);
    // React to cookie-consent change so GA loads on accept
    window.addEventListener('storage', onSave);
    window.addEventListener('cookie-consent-updated', onSave);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('cms-config-updated', onSave);
      window.removeEventListener('storage', onSave);
      window.removeEventListener('cookie-consent-updated', onSave);
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (navFont && navFont !== 'inherit') ensureGoogleFont(navFont);
  }, [navFont]);

  useEffect(() => {
    if (metadata?.favicon) setFavicon(metadata.favicon);
  }, [metadata]);

  useEffect(() => {
    if (gaId) syncGoogleAnalyticsConsent(gaId);
  }, [gaId]);

  return null;
};

export default ThemeApplier;
export { applyTheme, applyMetadata, ensureGoogleFont, setFavicon };
